import { Component, createRef } from 'react';

import ColorUtils from '../../../utils/color.utils';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import Modal from '../modal/modal.component';
import styles from './alert-dialog.component.module.scss';

const defaultAlertOptions = {
    type: 'success',
    confirmButtonText: 'Ok',
    confirmButtonClass: 'btn btn-primary me-2',
    cancelButtonText: 'Dismiss',
    cancelButtonClass: 'btn btn-secondary',
}

interface AlertDialogProps {
    title: string,
    text?: string,
    html?: string,
    type?: 'danger' | 'warning' | 'info' | 'success',
    confirmButtonText?: string,
    confirmButtonClass?: string,
    confirmButtonCallback?: () => void;
    showCancelButton?: boolean,
    cancelButtonText?: string,
    cancelButtonClass?: string,
    cancelButtonCallback?: () => void;
    input?: 'email' | 'number' | 'text',
    inputPlaceholder?: string,
    inputValidator?: (value) => boolean,
    /** Called once when the dialog goes away, by any route (a button, Escape, `closeRef`'s function). */
    onClosed?: () => void,
    /** Receives a function that closes the dialog from outside (fu-r2: the offline upload dialog, on `online`). */
    closeRef?: (close: () => void) => void,
    /** UXFU2-03: where focus goes on close when nothing opened the dialog (see Modal `returnFocusTo`). */
    returnFocusTo?: () => HTMLElement | null | undefined,
}

let alertSeq = 0;

export default class AlertDialog extends Component<AlertDialogProps> {
    modalRef = createRef<Modal>();
    /** UX4-04: the h2 and the message name and describe the dialog (the generic Modal only knows `data-modalheader`). */
    private readonly titleId = `alert-dialog-title-${++alertSeq}`;
    private readonly messageId = `alert-dialog-message-${alertSeq}`;

    public static create(props: AlertDialogProps) {
        const initialState = props;
        Modal.createModal(AlertDialog, initialState);
    }

    constructor(props: AlertDialogProps) {
        super(props);

    }

    componentDidMount() {
        this.props.closeRef?.(() => this.modalRef.current?.unmount());
    }

    private closedOnce = false;
    private notifyClosed = () => {
        if (this.closedOnce) return;
        this.closedOnce = true;
        this.props.onClosed?.();
    };

    getOptionsFromProps(props: AlertDialogProps): AlertDialogProps {
        const mergedOptions = Object.assign({}, defaultAlertOptions, props);
        return mergedOptions;
    }

    getSvgIconForType(type: 'danger' | 'warning' | 'info' | 'success') {
        const strokeWidth = 2;
        switch (type) {
            case 'danger':
                return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>;
            case 'warning':
                return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>;
            case 'info':
                return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>;
            default: // success
                return <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={strokeWidth}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>;
        }
    }

    getIconTextColorFromType(type: 'danger' | 'warning' | 'info' | 'success') {
        if (type) {
            return `text-${type}`;
        }
        return 'text-primary';
    }

    getMainIcon(type: 'danger' | 'warning' | 'info' | 'success') {
        // Use background color with opacity instead of border for a modern look
        const color = ColorUtils.getColor(type);
        const style = {
            backgroundColor: `${color}33`, // 20% opacity (approx 0.2 * 255 = 51 -> 0x33)
            color: color
        };

        return <div className={`${styles['alert-icon']}`} style={style}>
            {this.getSvgIconForType(type)}
        </div>
    }

    getOkButtonBgColorClassFromType(type: 'danger' | 'warning' | 'info' | 'success') {
        if (type === 'danger') {
            return 'btn-danger';
        }
        return 'btn-primary';
    }

    dismissClose() {
        if (this.props.cancelButtonCallback) {
            this.props.cancelButtonCallback();
        }
        this.modalRef.current.unmount();
    }

    confirmClose() {
        if (this.props.confirmButtonCallback) {
            this.props.confirmButtonCallback();
        }
        this.modalRef.current.unmount();
    }

    /**
     * UX4-04: Escape answers "No" when there is a No (cancel) button. With only an Ok, Escape dismisses a plain notice;
     * an Ok that runs something (its callback) must be chosen, so Escape is ignored there, as before.
     */
    escapeClose() {
        const options = this.getOptionsFromProps(this.props);
        if (options.showCancelButton) {
            this.dismissClose();
        } else if (!this.props.confirmButtonCallback) {
            this.modalRef.current?.unmount();
        }
    }

    render() {
        const options = this.getOptionsFromProps(this.props);
        const textId = `${this.messageId}-text`;
        const htmlId = `${this.messageId}-html`;
        const describedBy = [this.props.text && textId, this.props.html && htmlId].filter(Boolean).join(' ') || undefined;
        return <Modal ref={this.modalRef} disableCloseButton={true} modalClass={'alert-dialog fit'}
            role="alertdialog" labelledBy={this.titleId} describedBy={describedBy} onEscape={() => this.escapeClose()}
            onClose={this.notifyClosed} returnFocusTo={this.props.returnFocusTo}>
            <div data-modalbody style={{ textAlign: 'center' }}>
                {this.getMainIcon(options.type)}
                <h2 id={this.titleId} style={{ margin: '0 0 12px', fontSize: '1.25rem', fontWeight: 700, color: '#32325d' }}>{this.props.title}</h2>
                {
                    this.props.text && <p id={textId} style={{ margin: 0, color: '#525f7f', lineHeight: 1.5 }}>{this.props.text}</p>
                }
                {
                    this.props.html && <p id={htmlId} style={{ margin: 0, color: '#525f7f', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: this.props.html }}></p>
                }
            </div>
            <div data-modalfooter>
                {/* UXM-03: both keys take the 44 px coarse-pointer floor (`.footer-btn`); the dialog is an overlay, so the
                    taller footer moves nothing on the page. */}
                <button key={'confirmation-button'} onClick={() => this.confirmClose()}
                    className={`x-btn x-btn-sm x-btn-gray${this.getOkButtonBgColorClassFromType(options.type)} ${styles['footer-btn']}`}>
                    {options.confirmButtonText || 'Ok'}
                </button>
                {
                    options.showCancelButton && <button className={`x-btn x-btn-sm x-btn-gray btn-secondary ms-2 ${styles['footer-btn']}`} onClick={() => this.dismissClose()}>
                        {options.cancelButtonText || 'Dismiss'}
                    </button>
                }
            </div>
        </Modal>
    }
}